import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Calendar, RefreshCw, Cloud, CloudOff, X } from 'lucide-react';
import { KanbanBoard } from './KanbanBoard';
import { useReminders, ReminderCard, Priority, ReminderColumnKey } from '../../hooks/useReminders';

const PRIORITY_STYLES: Record<string, string> = {
  alta: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  media: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  bassa: 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300',
};

function toInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
function formatDue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function isOverdue(iso: string | null): boolean {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

function CardView({ card, onClick }: { card: ReminderCard; onClick: () => void }) {
  const overdue = isOverdue(card.due) && !card.completed;
  return (
    <button
      onClick={onClick}
      className="w-full cursor-pointer rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
    >
      <div className={`text-sm font-medium ${card.completed ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
        {card.title || '(senza titolo)'}
      </div>
      {card.description && (
        <div className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{card.description}</div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {card.priority && (
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${PRIORITY_STYLES[card.priority]}`}>
            {card.priority}
          </span>
        )}
        {card.due && (
          <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${
              overdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400'
            }`}
          >
            <Calendar className="h-3 w-3" />
            {formatDue(card.due)}
          </span>
        )}
      </div>
    </button>
  );
}

interface FormState {
  id?: string;
  title: string;
  description: string;
  due: string;
  priority: Priority;
}

function ReminderModal({
  initial,
  onClose,
  onSave,
  onDelete,
}: {
  initial: FormState;
  onClose: () => void;
  onSave: (f: FormState) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch {
      /* toast gestito nel hook */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isEdit ? 'Modifica promemoria' : 'Nuovo promemoria'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3">
          <input
            autoFocus
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Titolo"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Note"
            rows={3}
            className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
          />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Scadenza</label>
              <input
                type="datetime-local"
                value={toInput(form.due || null)}
                onChange={(e) => setForm({ ...form, due: fromInput(e.target.value) || '' })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              />
            </div>
            <div className="w-32">
              <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Priorità</label>
              <select
                value={form.priority || ''}
                onChange={(e) => setForm({ ...form, priority: (e.target.value || null) as Priority })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              >
                <option value="">Nessuna</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="bassa">Bassa</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          {isEdit && onDelete ? (
            <button
              onClick={async () => {
                await onDelete();
                onClose();
              }}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" /> Elimina
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800">
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving || !form.title.trim()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-gray-900"
            >
              {saving ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PromemoriaBoard: React.FC = () => {
  const { columns, meta, loading, error, reload, createReminder, updateReminder, moveReminder, deleteReminder } = useReminders();
  const [modal, setModal] = useState<FormState | null>(null);

  const boardColumns = useMemo(
    () => columns.map((c) => ({ id: c.key, title: c.title, cards: c.cards })),
    [columns]
  );

  const columnOf = (cardId: string): ReminderColumnKey | null => {
    for (const c of columns) if (c.cards.some((k) => k.id === cardId)) return c.key;
    return null;
  };

  const handleMove = (cardId: string, toColumnId: string) => {
    if (columnOf(cardId) === toColumnId) return; // reorder nella stessa colonna: nessuno stato da cambiare
    moveReminder(cardId, toColumnId as ReminderColumnKey);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {meta?.configured ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <Cloud className="h-3.5 w-3.5" /> Sincronizzato con iCloud
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-gray-700 dark:text-slate-300">
              <CloudOff className="h-3.5 w-3.5" /> iCloud non configurato
            </span>
          )}
          {meta?.lastSync?.started_at && (
            <span className="text-xs text-slate-400">
              ultima sync: {new Date(meta.lastSync.started_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" /> Aggiorna
          </button>
          <button
            onClick={() => setModal({ title: '', description: '', due: '', priority: null })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-gray-900"
          >
            <Plus className="h-4 w-4" /> Nuovo promemoria
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Caricamento…</div>
      ) : (
        <KanbanBoard
          columns={boardColumns}
          getCardId={(c: ReminderCard) => c.id}
          onCardMove={handleMove}
          renderCard={(card: ReminderCard) => (
            <CardView
              card={card}
              onClick={() =>
                setModal({
                  id: card.id,
                  title: card.title || '',
                  description: card.description || '',
                  due: card.due || '',
                  priority: card.priority,
                })
              }
            />
          )}
        />
      )}

      {modal && (
        <ReminderModal
          initial={modal}
          onClose={() => setModal(null)}
          onSave={async (f) => {
            if (f.id) {
              await updateReminder(f.id, {
                title: f.title,
                description: f.description || null,
                due: f.due || null,
                priority: f.priority,
              });
            } else {
              await createReminder({
                title: f.title,
                description: f.description || undefined,
                due: f.due || null,
                priority: f.priority,
              });
            }
          }}
          onDelete={modal.id ? async () => deleteReminder(modal.id!) : undefined}
        />
      )}
    </div>
  );
};

export default PromemoriaBoard;
